/**
 * Referral System Types
 *
 * Types for referral tracking and rewards.
 */

// ============================================================================
// Core Types
// ============================================================================

export interface ReferralCode {
  code: string;
  userId: string;
  userType: 'startup' | 'investor';
  createdAt: string;
  expiresAt?: string;
  maxUses?: number;
  currentUses: number;
  isActive: boolean;
}

export interface Referral {
  id: string;
  referrerId: string;
  referrerType: 'startup' | 'investor';
  refereeId: string;
  refereeType: 'startup' | 'investor';
  code: string;
  status: ReferralStatus;
  createdAt: string;
  convertedAt?: string;
  rewardAppliedAt?: string;
}

export type ReferralStatus =
  | 'pending'      // Signed up but not subscribed
  | 'converted'    // Referree subscribed (paid plan)
  | 'rewarded'     // Referrer received discount
  | 'expired'      // Referral expired (30 days window)
  | 'cancelled';   // Referree cancelled before reward

export interface ReferralReward {
  id: string;
  referralId: string;
  referrerId: string;
  type: 'discount_percent' | 'discount_amount' | 'free_month';
  value: number; // 20 for 20%, 2900 for CHF 29, 1 for 1 month
  appliedAt?: string;
  expiresAt: string;
  usedAt?: string;
  status: 'pending' | 'active' | 'used' | 'expired';
}

export interface ReferralStats {
  totalReferrals: number;
  pendingReferrals: number;
  convertedReferrals: number;
  totalRewardsEarned: number; // in cents
  rewardsUsed: number;
  rewardsPending: number;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Referral reward: 20% discount on next billing cycle
 */
export const REFERRAL_DISCOUNT_PERCENT = 20;

/**
 * Referral code format: PREFIX-XXXXXX (6 random alphanumeric)
 */
export const REFERRAL_CODE_LENGTH = 6;
export const REFERRAL_CODE_PREFIX_STARTUP = 'INV';
export const REFERRAL_CODE_PREFIX_INVESTOR = 'FND';

/**
 * Referral window: 30 days for referee to convert
 */
export const REFERRAL_CONVERSION_WINDOW_DAYS = 30;

/**
 * Reward expiry: 90 days to use discount
 */
export const REFERRAL_REWARD_EXPIRY_DAYS = 90;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate a random referral code
 */
export function generateReferralCode(userType: 'startup' | 'investor'): string {
  const prefix = userType === 'startup' ? REFERRAL_CODE_PREFIX_STARTUP : REFERRAL_CODE_PREFIX_INVESTOR;
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < REFERRAL_CODE_LENGTH; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${prefix}-${code}`;
}

/**
 * Calculate if a referral is still within conversion window
 */
export function isWithinConversionWindow(createdAt: string): boolean {
  const created = new Date(createdAt);
  const now = new Date();
  const daysDiff = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  return daysDiff <= REFERRAL_CONVERSION_WINDOW_DAYS;
}

/**
 * Calculate if a reward is still valid
 */
export function isRewardValid(reward: ReferralReward): boolean {
  if (reward.status !== 'active') return false;
  const expires = new Date(reward.expiresAt);
  return new Date() < expires;
}

/**
 * Format discount display text
 */
export function formatReferralDiscount(): string {
  return `${REFERRAL_DISCOUNT_PERCENT}% off`;
}
